/**
 * Run state: 3-minute timer, base+combo scoring, overflow fail.
 * Pure logic — no rendering, no physics.
 */
export const RUN_SECONDS = 180;
export const OVERFLOW_LIMIT = 120;
export const COMBO_WINDOW = 4; // seconds to keep the chain alive
export const COMBO_CAP = 10;
export const BASE_POINTS = 100;

export type RunPhase = "playing" | "ended";
export type EndReason = "cash-out" | "death"; // timer out | overflow

export class RunState {
  phase: RunPhase = "playing";
  endReason: EndReason | null = null;

  timeLeft = RUN_SECONDS;
  elapsed = 0;

  score = 0;
  combo = 0;
  bestCombo = 0;
  comboTimer = 0;
  incinerated = 0;
  portalTraversals = 0;

  private endCallback: ((reason: EndReason) => void) | null = null;

  onEnd(cb: (reason: EndReason) => void): void {
    this.endCallback = cb;
  }

  /** 0..1 progress through the run (drives the spawn ramp). */
  rampT(): number {
    return Math.min(1, this.elapsed / RUN_SECONDS);
  }

  /** 0..1 remaining combo window (drives the HUD decay bar). */
  comboFrac(): number {
    return this.combo > 0 ? this.comboTimer / COMBO_WINDOW : 0;
  }

  multiplier(): number {
    return Math.max(1, Math.min(this.combo, COMBO_CAP));
  }

  update(dt: number, liveTrash: number): void {
    if (this.phase !== "playing") return;
    this.elapsed += dt;
    this.timeLeft = Math.max(0, RUN_SECONDS - this.elapsed);

    if (this.comboTimer > 0) {
      this.comboTimer -= dt;
      if (this.comboTimer <= 0) this.combo = 0;
    }

    if (liveTrash >= OVERFLOW_LIMIT) this.end("death");
    else if (this.timeLeft <= 0) this.end("cash-out");
  }

  onIncinerate(): number {
    if (this.phase !== "playing") return 0;
    this.combo += 1;
    this.bestCombo = Math.max(this.bestCombo, this.combo);
    this.comboTimer = COMBO_WINDOW;
    this.incinerated += 1;
    const points = BASE_POINTS * this.multiplier();
    this.score += points;
    return points;
  }

  onTraversal(): void {
    this.portalTraversals += 1;
  }

  private end(reason: EndReason): void {
    if (this.phase === "ended") return;
    this.phase = "ended";
    this.endReason = reason;
    this.endCallback?.(reason);
  }

  reset(): void {
    this.phase = "playing";
    this.endReason = null;
    this.timeLeft = RUN_SECONDS;
    this.elapsed = 0;
    this.score = 0;
    this.combo = 0;
    this.bestCombo = 0;
    this.comboTimer = 0;
    this.incinerated = 0;
    this.portalTraversals = 0;
  }
}
